import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Categories() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleSelect = (key: string, url: string) => {
    setSelected(key);
    setOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="categories" className="scroll-mt-20 py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('categories.title')}</h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600">{t('categories.subtitle')}</p>
        </div>

        {/* Dropdown */}
        <div className="mt-8 max-w-md mx-auto">
          <label className="block text-sm font-medium text-foreground-700 mb-2">{t('categories.dropdownLabel')}</label>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 hover:border-primary-400 cursor-pointer"
            >
              <span className="truncate">{selected ? t(selected) : t('categories.dropdownPlaceholder')}</span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${open ? 'rotate-180' : ''}`}></i>
            </button>

            {open && (
              <div className="absolute z-30 mt-1 w-full bg-background-50 border border-background-200 rounded-lg py-1 max-h-80 overflow-y-auto">
                {categories.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => handleSelect(c.key, c.url)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 text-left cursor-pointer"
                  >
                    <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 overflow-hidden shrink-0">
                      <img src={c.image} alt={t(c.key)} className="w-full h-full object-cover object-top" />
                    </span>
                    <span className="truncate">{t(c.key)}</span>
                    <i className="ri-external-link-line ml-auto text-foreground-400"></i>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category grid buttons */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {categories.map((c) => (
            <a
              key={c.key}
              href={c.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex items-center gap-3 p-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-12 h-12 flex items-center justify-center rounded-md bg-background-100 overflow-hidden shrink-0">
                <img
                  src={c.image}
                  alt={t(c.key)}
                  title={`${t(c.key)} USFans Spreadsheet`}
                  className="w-full h-full object-cover object-top"
                />
              </span>
              <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 whitespace-nowrap overflow-hidden text-ellipsis">
                {t(c.key)}
              </span>
              <i className="ri-arrow-right-up-line ml-auto text-foreground-300 group-hover:text-primary-500"></i>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}